import { useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { SongPreview } from '~/interfaces';
import { useRegister } from '~/modules/hooks/use-keyboard-nav';
import useTaggedSongIndex from '~/modules/songs/hooks/use-tagged-song-index';
import isE2E from '~/modules/utils/is-e2-e';
import scrollIntoView from '~/modules/utils/scroll-into-view';

const MAX_SIBLINGS = 12;

interface Props {
  song: SongPreview;
  /** Whether the lane itself holds the card's keyboard focus, which is what ◀ ▶ then move inside. */
  focused: boolean;
  onSelect: (songId: string) => void;
}

/**
 * More of whatever the open song belongs to, as a lane inside the card rather than a jump into the
 * list - picking one swaps the card over to it and leaves everything else where it was.
 *
 * The series if the song has one, and the artist if it does not: only 217 of 6,243 songs carry a
 * curated series, so keying the lane off that alone left it off nine cards in ten. The artist is
 * the weaker grouping - a soundtrack credited to three acts does not hold together under it, which
 * is what the curated series is for - but it is the one the catalogue always knows.
 *
 * One focus stop for the whole lane, moved through with ◀ ▶, so nine siblings cost the phone one
 * mirrored row instead of nine.
 */
export default function SeriesLane({ song, focused, onSelect }: Props) {
  const register = useRegister();
  const { data } = useTaggedSongIndex();
  const series = song.series?.[0];
  const group = series ?? song.artist;
  const lane = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState(0);
  // Picking a sibling swaps the card over to it, and the lane is then that song's siblings - a
  // different set, so where the cursor had got to in the old one means nothing in the new one.
  const [lastSongId, setLastSongId] = useState(song.id);
  if (lastSongId !== song.id) {
    setLastSongId(song.id);
    setCursor(0);
  }

  const siblings = useMemo(
    () =>
      data
        .filter(
          (candidate) =>
            candidate.id !== song.id &&
            (series ? (candidate.series ?? []).includes(series) : candidate.artist === song.artist),
        )
        // The biggest series is eight songs, but the biggest artist is Lady Gaga's 46 - a lane that
        // long is 45 thumbnails fetched on opening a card, and 45 presses of ▶ to reach the end.
        .slice(0, MAX_SIBLINGS),
    [data, series, song.artist, song.id],
  );

  const position = Math.min(cursor, Math.max(siblings.length - 1, 0));
  const highlighted = siblings[position];

  const move = (direction: -1 | 1) =>
    setCursor((current) => Math.min(Math.max(current + direction, 0), siblings.length - 1));

  const canMove = focused && siblings.length > 1;
  useHotkeys('left', () => move(-1), { enabled: canMove }, [siblings.length]);
  useHotkeys('right', () => move(1), { enabled: canMove }, [siblings.length]);

  useEffect(() => {
    scrollIntoView(lane.current?.children[position], { block: 'nearest', inline: 'center' });
  }, [position]);

  if (siblings.length === 0 || !highlighted) return null;

  // Mirrored as a button, not a switch: a tap opens that sibling's own card, and ◀ ▶ are host keys
  // the phone has no arrow pad for - a switch would promise the phone can step along the lane.
  const navProps = register('series-songs', () => onSelect(highlighted.id), highlighted.title, false, {
    control: { type: 'button', label: `${highlighted.title} (${group})` },
  });

  return (
    <div className="flex min-w-0 flex-col gap-3 lg:col-span-2" {...navProps}>
      <span className="typography text-md font-bold tracking-[0.06em] text-white/65 uppercase sm:text-xl">
        More from {group}
      </span>
      <div className="relative min-w-0">
        <div ref={lane} className="flex [scrollbar-width:none] gap-3.5 overflow-x-auto [&::-webkit-scrollbar]:hidden">
          {siblings.map((sibling, index) => (
            <div
              key={sibling.id}
              data-test={`series-song-${sibling.id}`}
              data-highlighted={index === position}
              // A pointer picks the card it is actually over. Stopped from bubbling because the lane
              // is one registered control whose own handler acts on the HIGHLIGHTED sibling - so a
              // click on the third card used to open the first, whichever one the arrows had left
              // the cursor on.
              onClick={(event) => {
                event.stopPropagation();
                onSelect(sibling.id);
              }}
              // Amber only while the lane itself holds the focus — otherwise the card the arrows
              // last stopped on would read as the thing Enter is about to act on. The pointer's own
              // hint is the same white ring the buttons use, and it is kept off the amber card for
              // the same reason: white must not stand where "Enter acts here" already does.
              className={`flex w-[9.375rem] shrink-0 cursor-pointer flex-col gap-1.5 rounded-[0.625rem] border-2 p-[0.1875rem] sm:w-[14.5rem] ${
                index !== position ? 'border-transparent' : focused ? 'border-amber-400' : 'border-white/25'
              } ${isE2E() || (focused && index === position) ? '' : 'hover:hover-hint'}`}>
              <div
                className="aspect-video rounded-md bg-[#2b2b2b] bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url('https://i3.ytimg.com/vi/${sibling.video}/hqdefault.jpg')` }}
              />
              <span
                className={`truncate px-1 pb-0.5 text-sm ${index === position ? 'text-white/85' : 'text-white/60'}`}>
                {sibling.title}
              </span>
            </div>
          ))}
        </div>
        {/* The lane runs off the edge of the card rather than stopping short, so the last card is
            faded out to say there is more of it than fits. */}
        <div className="pointer-events-none absolute inset-y-0 right-0 w-[6.875rem] bg-gradient-to-r from-transparent to-slate-800" />
      </div>
    </div>
  );
}
